import { EyeIcon } from "@phosphor-icons/react/dist/csr/Eye";
import { LockIcon } from "@phosphor-icons/react/dist/csr/Lock";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";

interface FormulaBarStatusProps {
  coordinate: string | null;
  errorCode: string | null;
  isCellLocked: boolean;
  isSheetReadOnly: boolean;
  isRecalculating: boolean;
  hasUnsavedChanges?: boolean;
  isRetryingUnsavedChanges?: boolean;
  onRetryUnsavedChanges?: () => void;
  operationError?: string | null;
  onRetryOperation?: () => void;
}

export const FormulaBarStatus = ({
  coordinate,
  errorCode,
  isCellLocked,
  isSheetReadOnly,
  isRecalculating,
  hasUnsavedChanges = false,
  isRetryingUnsavedChanges = false,
  onRetryUnsavedChanges,
  operationError,
  onRetryOperation,
}: FormulaBarStatusProps) => (
  <>
    {hasUnsavedChanges && (
      <Stack
        direction="row"
        align="center"
        gap={1}
        aria-live="polite"
        title="Some edits in this sheet are not saved. Retry to save them."
        className="shrink-0 border-l border-line px-3 text-sm text-danger"
      >
        <WarningCircleIcon className="h-3.5 w-3.5" aria-hidden="true" />
        Not saved
        {onRetryUnsavedChanges && (
          <Button
            variant="link"
            size="xs"
            disabled={isRecalculating || isRetryingUnsavedChanges}
            onClick={onRetryUnsavedChanges}
          >
            Retry
          </Button>
        )}
      </Stack>
    )}
    {errorCode && (
      <Stack
        align="start"
        justify="center"
        gap={0}
        className="shrink-0 border-l border-line px-3 text-sm font-semibold text-danger"
      >
        {errorCode}
      </Stack>
    )}
    {coordinate && isCellLocked && (
      <TooltipProvider delay={200}>
        <Tooltip>
          <TooltipTrigger
            render={
              <span
                tabIndex={0}
                role="img"
                aria-label="Read only cell"
                className="flex shrink-0 items-center justify-center border-l border-line px-3 text-subtle focus-visible:ring-2 focus-visible:ring-focus focus-visible:outline-hidden focus-visible:ring-inset"
              />
            }
          >
            <LockIcon className="h-4 w-4" aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent side="bottom">Read only cell</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
    {isSheetReadOnly && (
      <Stack
        direction="row"
        align="center"
        gap={1}
        className="shrink-0 border-l border-line px-3 text-sm text-subtle"
      >
        <EyeIcon className="h-3.5 w-3.5" />
        View only
      </Stack>
    )}
    {isRecalculating && (
      <Stack
        direction="row"
        align="center"
        gap={1}
        aria-live="polite"
        className="shrink-0 border-l border-line px-3 text-sm text-subtle"
      >
        <Spinner aria-hidden size={14} />
        Calculating…
      </Stack>
    )}
    {operationError && (
      <Stack
        direction="row"
        align="center"
        gap={1}
        aria-live="polite"
        className="shrink-0 border-l border-line px-3 text-sm text-danger"
      >
        <WarningCircleIcon className="h-3.5 w-3.5" />
        <span className="max-w-72 truncate" title={operationError}>
          {operationError}
        </span>
        {onRetryOperation && (
          <Button variant="link" size="xs" onClick={onRetryOperation}>
            Retry
          </Button>
        )}
      </Stack>
    )}
  </>
);
